import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PropsWithClassName } from "@/types/PropsWithClassName";
import { cn } from "@/utils/classNames.ts";
import dayjs from "$/utils/dayjs/index.ts";

type Props = PropsWithClassName & {
  startDate: string;
  estimatedEndDate: string;
  destination?: string | null;
};

const STATUS_KEYS = [
  "reading_travel_dna",
  "remembering_past_refinements",
  "planning_your_trip",
  "finding_restaurants",
] as const;

const TripIsGenerated: FC<Props> = ({
  className,
  startDate,
  estimatedEndDate,
  destination,
}) => {
  const { t } = useTranslation();
  const [statusIndex, setStatusIndex] = useState(0);
  const [current, setCurrent] = useState(dayjs.utc().valueOf() / 1000);

  const start = dayjs.utc(startDate).valueOf() / 1000;
  const end = dayjs.utc(estimatedEndDate).valueOf() / 1000;
  const duration = Math.max(end - start, 1);
  const progress = Math.max(0, Math.min(1, (current - start) / duration));
  const percent = progress * 100;

  const statusMessages = useMemo(
    () => ({
      reading_travel_dna: t(
        "tripIsGeneratedComponent.reading_travel_dna",
        "Reading your Travel DNA…",
      ),
      remembering_past_refinements: t(
        "tripIsGeneratedComponent.remembering_past_refinements",
        "Remembering past refinements…",
      ),
      planning_your_trip: destination
        ? t("tripIsGeneratedComponent.planning_your_trip", {
            destination,
            defaultValue: "Planning your perfect trip to {{destination}}…",
          })
        : t(
            "tripIsGeneratedComponent.planning_your_trip_without_destination",
            "Planning your perfect trip…",
          ),
      finding_restaurants: t(
        "tripIsGeneratedComponent.finding_restaurants",
        "Finding restaurants that feel like you…",
      ),
    }),
    [destination, t],
  );

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrent(dayjs.utc().valueOf() / 1000);
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setStatusIndex((previous) => (previous + 1) % STATUS_KEYS.length);
    }, 4000);

    return () => window.clearInterval(interval);
  }, []);

  const currentStatus = statusMessages[STATUS_KEYS[statusIndex]];

  return (
    <div
      className={cn(
        "flex grow flex-col items-center justify-center gap-8 p-4 text-center",
        className,
      )}
    >
      <div className="flex flex-col gap-3">
        <h1 className="font-feature text-3xl font-semibold italic text-[#191919] md:text-4xl">
          {t(
            "tripIsGeneratedComponent.header",
            "Cody is shaping your itinerary…",
          )}
        </h1>
        <p className="font-sans text-sm text-text-secondary">
          {t(
            "tripIsGeneratedComponent.subheader",
            "Building a trip around your Travel DNA. This usually takes 1–2 minutes.",
          )}
        </p>
      </div>

      <div className="flex w-full max-w-[460px] flex-col gap-4">
        <div
          className="h-3 w-full overflow-hidden rounded-full bg-base-300"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percent)}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-1000 ease-linear"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p
          key={STATUS_KEYS[statusIndex]}
          className="min-h-5 font-sans text-xs text-[#191919]"
          aria-live="polite"
        >
          {currentStatus}
        </p>
      </div>
    </div>
  );
};

export default TripIsGenerated;